import {useMemo} from 'react'
import {useQueries} from '@github-ui/react-query'
import {useRouteQuery, useSuspenseRouteQuery} from '@github-ui/react-core/future/use-route-query'
import {issuesDashboardSurfaceContentRoute} from '../routes/issues-dashboard-surface-content-route'
import type {IssuesDashboardEnrichedDataPayload} from '../types/routes-types'
import type {IssueDeferredItem} from '@github-ui/shared-dashboard-lists/issues/types/list-types'
import type {DeferredItem} from '@github-ui/shared-dashboard-lists/pulls/types/list-types'
import {getListItemIdentity} from '@github-ui/shared-dashboard-lists/shared/helpers/item-identity'

import {enrichIssueItem, enrichPullItem, loadRowNode, rowNodeKey, rowNodeQueryKey} from './issues-dashboard-row-node'

type EnrichedRow = IssuesDashboardEnrichedDataPayload['issues'][number]

type MergeKeyable = {itemType: 'issue' | 'pull_request'; id: number | string; globalId?: string}

// The composite key is always available (itemType + id) and is itself
// collision-safe across the two id spaces, so it is the reliable fallback.
function compositeKey(item: MergeKeyable): string {
  return `${item.itemType}:${item.id}`
}

// Index deferred results under BOTH their composite key and their global
// identity (`getListItemIdentity`, when it differs from the composite key).
// This keeps the merge correct during backend deploy skew, where the two
// separately-fetched payloads (main vs deferred) may disagree on whether a
// string `id`/`globalId` is present: the composite key always matches, and
// the global identity is used when a side has it. Composite (`pull_request:5`)
// and global identity (`PR_kw…`) never collide, and each is unique per row.
function buildDeferredIndex<T extends MergeKeyable>(results: T[]): Map<string, T> {
  const map = new Map<string, T>()
  for (const result of results) {
    map.set(compositeKey(result), result)
    map.set(getListItemIdentity(result), result)
  }
  return map
}

export function useEnrichedIssues(): IssuesDashboardEnrichedDataPayload {
  const mainQuery = useSuspenseRouteQuery(issuesDashboardSurfaceContentRoute, 'mainQuery')
  const deferredQuery = useRouteQuery(issuesDashboardSurfaceContentRoute, 'deferred')

  const mainResults = mainQuery.data?.results
  const deferredResults = deferredQuery.data?.payload?.issuesDashboardSurfaceContentDeferredData.results

  // The list (root) merge. This is the seed for the per-row node queries below.
  const listRows = useMemo<EnrichedRow[]>(() => {
    const deferredIndex = buildDeferredIndex(deferredResults || [])
    return mainResults.map(item => {
      // Prefer the global identity; always fall back to the composite key so a
      // one-sided string id/globalId (backend deploy skew) never drops the
      // deferred fields.
      const deferred = deferredIndex.get(getListItemIdentity(item)) ?? deferredIndex.get(compositeKey(item))
      if (item.itemType === 'pull_request') {
        return enrichPullItem(item, deferred as DeferredItem | undefined)
      }
      return enrichIssueItem(item, deferred as IssueDeferredItem | undefined)
    })
  }, [deferredResults, mainResults])

  // Per-row "node" queries seeded from the list. Each row that carries a signed
  // `aliveChannel` gets an enabled node query; a live update invalidates just
  // that row's node (see use-issues-dashboard-live-updates), refreshing one row
  // via GET /issues/_rows instead of refetching the whole list.
  const listUpdatedAt = mainQuery.dataUpdatedAt
  const nodeResults = useQueries({
    queries: listRows.map(item => ({
      queryKey: rowNodeQueryKey(rowNodeKey(item)),
      queryFn: async (): Promise<EnrichedRow> => {
        // Live-update rows always carry a resolvable global identity (the
        // backend emits it alongside the aliveChannel); fetch the fresh row by
        // that identity.
        if (item.aliveChannel) {
          return (await loadRowNode(getListItemIdentity(item))) ?? item
        }
        return item
      },
      enabled: !!item.aliveChannel,
      initialData: item,
      initialDataUpdatedAt: listUpdatedAt,
      // Never refetch on mount/focus/interval — only on explicit invalidation
      // from a live update (invalidation overrides staleTime and forces a refetch).
      staleTime: Number.POSITIVE_INFINITY,
    })),
  })

  // Prefer a node ONLY when it has been individually refetched more recently
  // than the list; otherwise the list is the fresher source (e.g. after a
  // filter/sort/pagination refetch), keeping the two coherent without having to
  // re-seed node caches. Cheap map over the current page's rows — no memo needed.
  const issues = listRows.map((item, index) => {
    const node = nodeResults[index]
    return node?.data && node.dataUpdatedAt > listUpdatedAt ? node.data : item
  })

  return {
    issues,
    pageInfo: mainQuery?.data?.pageInfo,
    isPending: deferredQuery.isPending,
    error: mainQuery.data.error || null,
  }
}
